import React, { Component } from 'react'
import { Header, Icon, Divider, Form, TextArea, Button, Segment, Label, Message, Tab } from 'semantic-ui-react'
import moment from 'moment/moment'
import GeneratedLicenseAdd from '../GeneratedLicenses/GeneratedLicenseAdd'
import LicenseReq from '../../containers/GeneratedLicenses/GeneratedLicenseReq'

// License checker: paste a signed license key (or drop a .lic file) and the
// backend decodes it, reconciles it with the database (source of truth) and
// returns the license details. Admin-only.
export default class LicenseChecker extends Component {
    constructor(props) {
        super(props)
        this.state = { text: '', dragOver: false, open: false }
    }

    componentDidMount() {
        // Start clean so a previously-decoded license isn't shown on open.
        if (this.props.clearDecodedLicense) this.props.clearDecodedLicense()
    }

    check = (text) => {
        const value = (text != null ? text : this.state.text).trim()
        if (!value) return
        this.props.decodeLicense(value)
    }

    onDrop = (e) => {
        e.preventDefault()
        this.setState({ dragOver: false })
        if (!this.props.isAdmin) return
        const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]
        if (!file) return
        const reader = new FileReader()
        reader.onload = (ev) => {
            const content = String(ev.target.result || '').replace(/\r\n?/g, '\n')
            this.setState({ text: content })
            // Auto-decode on drop; the backend tolerates the holder-id line.
            this.check(content)
        }
        reader.readAsText(file)
    }

    // Renew an expired license: open the Add-license wizard pre-filled from the
    // stored record (reuses the same flow as the "New license" report action).
    generateNew = () => {
        const d = this.props.decoded || {}
        const row = {
            id: d.licenseId,
            email: d.email,
            fullname: d.fullname || d.licenseHolder,
            company: d.company,
            license_type: d.productCode
        }
        Promise.resolve(this.props.startLicenseFromRow(row)).then(() => this.setState({ open: true }))
    }

    closeWizard = () => this.setState({ open: false })

    field = (label, value) => (
        <Form.Field>
            <label>{label}</label>
            <input value={value || ''} readOnly={true}/>
        </Form.Field>
    )

    renderResult() {
        const d = this.props.decoded
        if (!d) return null
        if (d.ErrorMsg && d.ErrorMsg !== 'NoError') {
            return <Message negative header='Could not read license' content={d.ErrorMsg}/>
        }
        const field = this.field
        const exp = moment(d.expireDate, ['DD.MM.YYYY HH:mm:ss', 'DD.MM.YYYY'])
        const expired = exp.isValid() ? exp.isBefore(moment()) : false

        const licensePane = (
            <Tab.Pane>
                <Form>
                    <Form.Group widths='equal'>
                        {field('License type', d.productName || d.licenseType)}
                        <Form.Field>
                            <label>Expire date</label>
                            <input value={d.expireDate || ''} readOnly={true}/>
                            {exp.isValid() &&
                                <Label basic color={expired ? 'red' : 'green'} pointing='left'>
                                    {expired ? 'Expired' : 'Valid'}
                                </Label>}
                        </Form.Field>
                    </Form.Group>
                    {field('HW information', d.hwInfo)}
                    {field('License holder', d.licenseHolder)}
                    <Form.Group widths='equal'>
                        {field('Origin', d.origin)}
                        {field('Create date', d.createDate)}
                    </Form.Group>
                </Form>
            </Tab.Pane>
        )

        const dbPane = (
            <Tab.Pane>
                <Form>
                    <Form.Group widths='equal'>
                        {field('Email', d.email)}
                        {field('Company', d.company)}
                    </Form.Group>
                    <Form.Group widths='equal'>
                        {field('Created by', d.createdBy)}
                        {field('Registered in database', d.registeredDate)}
                    </Form.Group>
                    <div>
                        {d.inDatabase &&
                            <Label color='green'><Icon name='database'/>In database</Label>}
                        {d.stored &&
                            <Label color='blue'><Icon name='plus'/>Added to database</Label>}
                        {!d.inDatabase && !d.stored &&
                            <Label>Not in database</Label>}
                    </div>
                </Form>
            </Tab.Pane>
        )

        const panes = [
            { menuItem: 'License', render: () => licensePane },
            { menuItem: 'Registration', render: () => dbPane }
        ]

        return (
            <Segment>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5em' }}>
                    <Label size='tiny'>Format: {d.format || 'unknown'}</Label>
                    <span>
                        <Button size='tiny' disabled={!this.props.isAdmin || !d.licenseId} onClick={this.generateNew}>
                            <Icon name='key'/> Create new license
                        </Button>
                        {d.licenseId &&
                            <span title='Show the original license request' style={{ marginLeft: '0.5em' }}>
                                <LicenseReq rowId={d.licenseId} licenseId={d.licenseHolder}/>
                                Original request
                            </span>}
                    </span>
                </div>

                {expired &&
                    <Message warning>
                        <Message.Header>This license has expired</Message.Header>
                        <p>Use "Create new license" above to issue a new, valid license for the same machine and product.</p>
                    </Message>}

                <Tab menu={{ secondary: true, pointing: true }} panes={panes}/>
            </Segment>
        )
    }

    render() {
        const { isAdmin } = this.props
        const { text, dragOver, open } = this.state
        return (
            <React.Fragment>
                <Header as='h2'>
                    <Icon name='shield'/>
                    <Header.Content>License checker</Header.Content>
                </Header>

                {!isAdmin &&
                    <Message info content='The license checker is available to administrators only.'/>}

                <Form>
                    <div onDrop={this.onDrop}
                         onDragOver={(e) => { e.preventDefault(); if (isAdmin && !dragOver) this.setState({ dragOver: true }) }}
                         onDragLeave={() => this.setState({ dragOver: false })}
                         style={dragOver ? { outline: '2px dashed #2185d0', outlineOffset: '2px', borderRadius: '4px' } : undefined}>
                        <Form.Field>
                            <label>License key</label>
                            <TextArea rows={8} style={{ fontFamily: 'Monospace' }}
                                      placeholder={'Paste a license key here, or drop a .lic file onto this box'}
                                      value={text}
                                      disabled={!isAdmin}
                                      onChange={(e, { value }) => this.setState({ text: value })}/>
                        </Form.Field>
                        <small style={{ color: 'rgba(0,0,0,0.5)' }}>Tip: drag a .lic file here to check it automatically.</small>
                    </div>
                    <Divider hidden/>
                    <Button disabled={!isAdmin || !text.trim()} onClick={() => this.check()}>
                        <Icon name='unlock'/> Check license
                    </Button>
                    <Button basic disabled={!text && !this.props.decoded}
                            onClick={() => { this.setState({ text: '' }); this.props.clearDecodedLicense() }}>
                        Clear
                    </Button>
                </Form>

                <Divider hidden/>
                {this.renderResult()}

                <GeneratedLicenseAdd open={open} onClose={this.closeWizard}/>
            </React.Fragment>
        )
    }
}
